import React, { useRef, useMemo } from 'react';
import { IconEdit, IconInputs } from 'components/Icons';
import { Button } from 'components/Button';
import { noop } from 'utils/fp';
import { useModalActions } from 'components/ModalManager/Context';
import { cx } from '@linaria/core';
import { flex } from 'styles/utils';
import dynamic from 'next/dynamic';
import { container, linePreview, lineCode, plusLines, editIcon, cursorPointer, inputFile, modalHeader, pyEditor } from './PythonUploadEdit.styles';

const MonacoEditor = dynamic(() => import('@monaco-editor/react'), { ssr: false });

const PYTHON_MODAL_ID = 'python_editor';

export const PythonUploadEdit = ({ value, onChange }: { value: string; onChange: (v?: string) => void }): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { openModal, closeModal } = useModalActions();

  const handleClose = (): void => closeModal({ id: PYTHON_MODAL_ID });

  const onFileChange = (): void => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      const result = reader.result as string;
      onChange(result);
    });

    const files = fileInputRef.current?.files;
    if (files) {
      reader.readAsText(files[0]);
    }
  };

  const handleShowModalEditor = (): void => {
    openModal({
      id: PYTHON_MODAL_ID,
      title: '',
      content: (
        <div data-testid="py-editor" className={pyEditor}>
          <div className={cx(flex.init, flex.justifyContent.between, flex.alignItems.center, modalHeader)}>
            <span>ClarifaIDE</span>
            <Button key="useModel" size="md" variant="primary" onClick={handleClose} aria-label="Confirm">
              Confirm
            </Button>
          </div>
          <MonacoEditor language="python" onChange={onChange} defaultValue={value as string} height={768} theme="vs-dark" />
        </div>
      ),
      makeActions: noop,
      closeOnBackdropClick: false,
      hideOnEsc: false,
    });
  };

  /* istanbul ignore next */
  const uploadClick = (): void => {
    // below code allows user to upload same file
    // without it, onChange wont be detected on same file upload
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const lineData = useMemo((): { firstLine: string; lineAmount: number } => {
    if (value) {
      const lines = (value as string).split('\n').filter((line) => line.trim().length);
      const firstLine = lines[0];
      const lineAmount = lines.length;

      return { firstLine, lineAmount };
    } else {
      return { firstLine: '', lineAmount: 0 };
    }
  }, [value]);

  const { firstLine, lineAmount } = lineData;

  let previewPlusLinesCount;

  switch (lineAmount) {
    case 0:
    case 1:
      previewPlusLinesCount = '';
      break;
    case 2:
      previewPlusLinesCount = '+1 LINE';
      break;
    default:
      previewPlusLinesCount = `+${lineAmount - 1} LINES`;
  }

  return (
    <div className={cx(flex.init, flex.justifyContent.center, flex.alignItems.center, container)}>
      <div className={cx(flex.init, flex.justifyContent.center, flex.alignItems.center, linePreview)}>
        <span className={cx(flex.init, flex.justifyContent.start, lineCode)}>{firstLine || 'no code entered...'}</span>
        <span className={cx(flex.init, flex.justifyContent.center, plusLines)}>{previewPlusLinesCount}</span>
      </div>

      <button type="button" data-testid="edit-py-icon" onClick={handleShowModalEditor} className={editIcon}>
        <IconEdit size={26} />
      </button>

      <label>
        <IconInputs size={26} className={cursorPointer} />
        <input
          data-testid="python-upload-file"
          ref={fileInputRef}
          type="file"
          accept=".py"
          className={inputFile}
          onClick={uploadClick}
          onChange={onFileChange}
        />
      </label>
    </div>
  );
};
